import { ConvexError } from "convex/values";
import { formatBillingDate } from "@/lib/billing-status";

// Customer-facing copy for member administration (CON-432). No prices, proration, or credit
// wording: seat changes use the approved "updates automatically" sentence only.

export type OrgRole = "owner" | "admin" | "member" | "reviewer";

export const ROLE_LABELS: Record<OrgRole, string> = {
  owner: "Owner",
  admin: "Admin",
  member: "Member",
  reviewer: "Reviewer",
};

export const ROLE_DESCRIPTIONS: Record<OrgRole, string> = {
  owner: "Full access: creates projects and manages members and billing. Uses a paid seat.",
  admin: "Full access: creates projects and manages members and billing. Uses a paid seat.",
  member: "Uploads documents, processes work, and edits results on assigned projects. Uses a paid seat.",
  reviewer: "Read-only: views assigned projects and downloads completed exports. Free.",
};

const SEAT_CHANGE_SENTENCE = "Your subscription updates automatically; any change appears on your next invoice.";

export function isPaidSeatRole(role: string | null | undefined) {
  return role === "owner" || role === "admin" || role === "member";
}

/** Short seat label shown beside a member's role. */
export function seatLabel(role: string | null | undefined) {
  if (isPaidSeatRole(role)) return "Paid seat";
  return role === "reviewer" ? "Free" : null;
}

/** What a role change means for the person, for the confirmation dialog. */
export function describeRoleChange(fromRole: string, toRole: OrgRole) {
  if (toRole === "reviewer" && isPaidSeatRole(fromRole)) {
    return `${ROLE_DESCRIPTIONS.reviewer} They'll no longer be able to create, upload, or process work.`;
  }
  return ROLE_DESCRIPTIONS[toRole];
}

function freeReviewerSeats(count: number) {
  return count === 1 ? "1 free reviewer seat" : `${count} free reviewer seats`;
}

export type ReviewerAllowance = { allowance: number; remaining: number; overAllowance?: boolean };

/** The company's free reviewer allowance. Pending reviewer invitations hold a seat. */
export function describeReviewerAllowance(usage: ReviewerAllowance) {
  if (usage.overAllowance) {
    return `Your company has more reviewers than its ${freeReviewerSeats(usage.allowance)}. Existing reviewers keep access, but you can't add more until some are removed.`;
  }
  if (usage.remaining === 0) {
    return `All ${freeReviewerSeats(usage.allowance)} are in use, including pending reviewer invitations.`;
  }
  return `${usage.remaining} of ${usage.allowance} free reviewer seats left. Pending reviewer invitations count toward this.`;
}

/** Mirrors the server's REVIEWER_LIMIT_REACHED message (reviewers.ts). */
export function reviewerLimitMessage(allowance: number | undefined) {
  const seats = allowance === undefined ? "free reviewer seats" : freeReviewerSeats(allowance);
  return (
    `All ${seats} are in use, including pending reviewer invitations. Remove a reviewer, revoke a pending ` +
    "reviewer invitation, or invite this person as a member, which uses a paid seat. Contact support if you " +
    "need more reviewer seats."
  );
}

const PAID_SEAT_CONFIRMATION_MESSAGE = "This change uses a paid seat. Confirm the paid seat, then try again.";

export function roleLabel(role: string | null | undefined) {
  return role && role in ROLE_LABELS ? ROLE_LABELS[role as OrgRole] : "None";
}

export type BillingConsequence =
  | "no_seat_change"
  | "adds_paid_seat"
  | "removes_paid_seat"
  | "blocked_pending_cancellation"
  | "enterprise_contract"
  | "covered_by_grant"
  | "requires_plan";

export type SeatBillingPreview = {
  billingConsequence: BillingConsequence;
  currentBillableSeats: number;
  billableSeatsAfter: number;
  cancellationEndsAt?: number | null;
};

const PAID_SEAT_BLOCKED = "PAID_SEAT_BLOCKED_PENDING_CANCELLATION";

/** Mirrors the server's PAID_SEAT_BLOCKED_PENDING_CANCELLATION message (billing/seatImpact.ts), with the end date. */
export function paidSeatBlockedMessage(cancellationEndsAt: number | null | undefined) {
  const ends = cancellationEndsAt ? `on ${formatBillingDate(cancellationEndsAt)}` : "at the end of the billing period";
  return (
    `Your subscription is set to end ${ends}, so you can't add owners, admins, or members. Resume your ` +
    "subscription to add paid seats. You can still invite reviewers for free."
  );
}

/** Shown with pending invitations while a cancellation is scheduled. */
export function pendingCancellationInvitationNote(cancellationEndsAt: number | null | undefined) {
  if (!cancellationEndsAt) return "Invitations accepted before your plan ends won't add charges.";
  const ends = formatBillingDate(cancellationEndsAt);
  return `Invitations accepted before ${ends} won't add charges; your plan ends on ${ends}.`;
}

export function describeBillingConsequence(preview: SeatBillingPreview) {
  const seats = `Paid seats: ${preview.currentBillableSeats} → ${preview.billableSeatsAfter}.`;
  switch (preview.billingConsequence) {
    case "no_seat_change":
      return "No change to paid seats.";
    case "adds_paid_seat":
    case "removes_paid_seat":
      return `${seats} ${SEAT_CHANGE_SENTENCE}`;
    case "blocked_pending_cancellation":
      return paidSeatBlockedMessage(preview.cancellationEndsAt);
    case "enterprise_contract":
      return `${seats} Your invoiced contract isn't changed automatically. Contact support if your seat count needs updating.`;
    case "covered_by_grant":
      return `${seats} Your company's current access covers this at no charge.`;
    case "requires_plan":
      return `${seats} Your company needs an active subscription before this person can edit.`;
  }
}

/** What accepting a paid invitation means for billing, shown in the invite form before sending. */
export function describePaidInvite(preview: SeatBillingPreview) {
  if (preview.billingConsequence === "adds_paid_seat") {
    return (
      `Uses a paid seat once they accept (paid seats: ${preview.currentBillableSeats} → ${preview.billableSeatsAfter}). ` +
      `Pending invitations are never billed. ${SEAT_CHANGE_SENTENCE}`
    );
  }
  return describeBillingConsequence(preview);
}

/** The dated message for a server PAID_SEAT_BLOCKED_PENDING_CANCELLATION error, or null for other errors. */
export function getPaidSeatBlockedMessage(error: unknown) {
  if (!(error instanceof ConvexError)) return null;
  const data = error.data as { code?: unknown; cancellationEndsAt?: unknown } | null;
  if (data?.code !== PAID_SEAT_BLOCKED) return null;
  return paidSeatBlockedMessage(typeof data.cancellationEndsAt === "number" ? data.cancellationEndsAt : null);
}

/** User-facing message for member-admin and invitation errors: known codes first, then the server's message. */
export function getMemberChangeErrorMessage(error: unknown, fallback: string) {
  const blocked = getPaidSeatBlockedMessage(error);
  if (blocked) return blocked;
  if (error instanceof ConvexError) {
    const data = error.data as { code?: unknown; message?: unknown; allowance?: unknown } | null;
    if (data?.code === "REVIEWER_LIMIT_REACHED") {
      return reviewerLimitMessage(typeof data.allowance === "number" ? data.allowance : undefined);
    }
    if (data?.code === "BILLABLE_SEAT_ACKNOWLEDGEMENT_REQUIRED") return PAID_SEAT_CONFIRMATION_MESSAGE;
    if (data && typeof data.message === "string") return data.message;
  }
  return fallback;
}

export function describeMemberChangeKind(kind: string) {
  switch (kind) {
    case "joined":
      return "Joined";
    case "removed":
      return "Removed";
    case "ownership_transferred":
      return "Made owner";
    default:
      return "Role changed";
  }
}

export function describeMemberChangeSource(source: string) {
  switch (source) {
    case "member_admin":
      return "Member admin";
    case "workos_event":
      return "Identity provider";
    default:
      return "Sign-in sync";
  }
}
